import { NavLink, useLocation } from "react-router-dom";
import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { APP_VERSION, CHANGELOG } from "../version";
import { useAuth } from "../context/AuthContext";
import StaffBrand from "./StaffBrand";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import ServiceRequestBadge from "./ServiceRequestBadge";
import { staffLinkMatches, visibleStaffNavigation } from "../utils/staffNavigation";
 
const linkBase = "block px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors";
const active = "bg-gray-200 dark:bg-blue-900 font-semibold";

function NavigationGroup({ group, linkClass, onNavigate, badgeEnabled }) {
  const { pathname } = useLocation();
  const id = useId();
  const selected = group.links.some(link => staffLinkMatches(pathname, link.to));
  const [disclosure, setDisclosure] = useState(null);
  const open = disclosure?.pathname === pathname ? disclosure.open : selected;
  return (
    <div className="staff-nav-group">
      <button type="button" className="staff-nav-heading" aria-expanded={open} aria-controls={id}
        data-selected={selected}
        onClick={() => setDisclosure({ pathname, open: !open })}>
        <span>{group.label}</span>
        {group.id === "wireless" && !open && <ServiceRequestBadge enabled={badgeEnabled} />}
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      <div id={id} hidden={!open}>
        {open && <div className="staff-nav-links">
          {group.links.map(link => (
            <NavLink key={link.to} to={link.to} className={linkClass} onClick={onNavigate}>
              {link.label}
              {link.serviceRequests && <ServiceRequestBadge enabled={badgeEnabled} />}
            </NavLink>
          ))}
        </div>}
      </div>
    </div>
  );
}

export default function Sidebar({ variant = "desktop", onNavigate } = {}) {
  const { user, logout, role } = useAuth();
  const linkClass = ({ isActive }) => `${linkBase} ${isActive ? active : ""}`;
 
  // Tile color computation moved to BrandProvider for earlier, centralized hydration
 

  const doLogout = async () => {
    try {
      if (logout) await logout();
      else await signOut(auth);
    } catch (e) {
      console.error("Sign out failed", e);
    }
  };

  const isMobile = variant === "mobile";
  const asideBase = isMobile
    ? "relative z-10 flex flex-col w-64 max-w-[80vw] h-full min-h-0 overflow-hidden p-4 safe-bottom"
    : "relative z-10 hidden lg:flex lg:flex-col w-60 h-screen min-h-0 overflow-hidden border-r p-4";

  const [showChangelog, setShowChangelog] = useState(false);

  return (
    <aside
      className={`staff-sidebar ${asideBase}`}
      /* Use dark mode surface colors permanently */
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--border)',
      }}
    >
      <div className="mb-4">
        <StaffBrand onNavigate={onNavigate} />
      </div>
      {/* Scrollable nav area */}
      <div className="staff-nav-scroll flex-1 min-h-0 overflow-y-auto pr-1 scrollbar-hidden">
        <nav className="space-y-1">
          <NavLink to="/" className={linkClass} end onClick={onNavigate}>Dashboard</NavLink>
          <NavLink to="/customers" className={linkClass} onClick={onNavigate}>Customers</NavLink>
          {visibleStaffNavigation(role).map(group => (
            <NavigationGroup key={group.id} group={group} linkClass={linkClass} onNavigate={onNavigate}
              badgeEnabled={!!user && ["staff", "admin", "owner"].includes(role)} />
          ))}
        </nav>
      </div>

      {/* Footer pinned to bottom */}
      <div className="mt-auto pt-3">
        {/* App version + small changelog toggle */}
        <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
          <span className="font-medium">Version:</span> {APP_VERSION}
          <button
            type="button"
            className="ml-2 px-2 py-0.5 rounded border text-xs bg-white hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
            onClick={() => setShowChangelog((v) => !v)}
            aria-expanded={showChangelog}
          >
            {showChangelog ? "Hide" : "Show"} changes
          </button>
        </div>
        {showChangelog && (
          <div className="mb-2 max-h-28 overflow-y-auto border rounded p-2 bg-white dark:bg-gray-900 dark:border-gray-700">
            {CHANGELOG.length === 0 ? (
              <div className="text-xs text-gray-600">No entries yet.</div>
            ) : (
              <ul className="text-xs list-disc pl-4 space-y-2">
                {CHANGELOG.map((e) => (
                  <li key={e.v}>
                    <div>
                      <span className="font-medium">{e.v}</span>
                      {e.date ? <span className="text-gray-500"> • {e.date}</span> : null}
                    </div>
                    {Array.isArray(e.notes) && e.notes.length > 0 && (
                      <ul className="list-disc pl-4 mt-1">
                        {e.notes.map((n, i) => (
                          <li key={i}>{n}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="text-xs text-gray-500 dark:text-gray-300 break-words mb-2">
          {user?.email || "Signed in"}
        </div>
        <button
          className="w-full px-3 py-2 rounded-lg text-white bg-brand-gradient hover:opacity-90"
          onClick={doLogout}
        >
          Sign out
        </button>
      </div>
    </aside>
  );
}
